import React from "react";
import { CheckCircle2, CircleDashed, Loader2, Send } from "lucide-react";
import { SlabButton } from "src/components/ui/SlabButton";

export type PublishStatus = "in_sync" | "unpublished_changes" | "empty_draft";

interface PublishCarouselButtonProps {
  status: PublishStatus;
  /** ISO string or epoch ms — when the last publish happened. */
  lastPublishedAt: number | null;
  /** "3 unpublished changes" — set when status === unpublished_changes. */
  changeSummary?: string | null;
  publishing: boolean;
  disabled?: boolean;
  onPublish: () => void;
}

function formatRelative(ms: number | null): string {
  if (!ms) return "never";
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  if (diff < 60 * 60 * 1000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.round(diff / 3_600_000)}h ago`;
  return `${Math.round(diff / 86_400_000)}d ago`;
}

const STATUS_PILL: Record<
  PublishStatus,
  { label: string; bg: string; fg: string; ring: string }
> = {
  in_sync: {
    label: "Live carousel in sync",
    bg: "bg-[#ECFDF5]",
    fg: "text-[#0F766E]",
    ring: "ring-[#14B8A6]/40",
  },
  unpublished_changes: {
    label: "Unpublished changes",
    bg: "bg-[#FEF3C7]",
    fg: "text-[#92400E]",
    ring: "ring-[#F59E0B]/40",
  },
  empty_draft: {
    label: "Draft is empty",
    bg: "bg-[#F1F5F9]",
    fg: "text-[#475569]",
    ring: "ring-[#94A3B8]/40",
  },
};

/**
 * Top-bar control for the carousel page. Shows a status pill (in-sync vs.
 * unpublished changes vs. empty), the timestamp of the last publish, and a
 * primary "Publish carousel" button that snapshots the current draft and
 * pushes it live atomically.
 */
export const PublishCarouselButton: React.FC<PublishCarouselButtonProps> = ({
  status,
  lastPublishedAt,
  changeSummary,
  publishing,
  disabled,
  onPublish,
}) => {
  const pill = STATUS_PILL[status];
  const Icon =
    status === "in_sync"
      ? CheckCircle2
      : status === "unpublished_changes"
        ? CircleDashed
        : CircleDashed;

  return (
    <div className="flex w-full flex-col items-stretch gap-3 rounded-[1.75rem] border-4 border-white/10 bg-white/5 p-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-wide ring-2 ${pill.bg} ${pill.fg} ${pill.ring}`}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {pill.label}
          </span>
          {changeSummary && status === "unpublished_changes" && (
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-extrabold text-white/90 ring-2 ring-white/20">
              {changeSummary}
            </span>
          )}
        </div>
        <p className="text-xs font-bold text-white/70 sm:text-sm">
          {lastPublishedAt
            ? `Last published ${formatRelative(lastPublishedAt)}`
            : "Publish your draft to go live for the first time."}
        </p>
      </div>
      <SlabButton
        type="button"
        variant="primary"
        disabled={disabled || publishing || status === "empty_draft"}
        onClick={onPublish}
        backgroundColor="#14B8A6"
        lipColor="#0F766E"
        shadowLiftPx={6}
        shadowLiftActivePx={2}
        className="disabled:opacity-60"
      >
        {publishing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            Publishing…
          </>
        ) : (
          <>
            <Send className="h-5 w-5" aria-hidden />
            Publish carousel
          </>
        )}
      </SlabButton>
    </div>
  );
};
